<template>
	<b-modal :title="readOnly ? 'Edit channel' : 'Add a channel'" ref="modal" :busy="loading>0">
		<div class="text-center" v-if="loading>0">
			<b-icon icon="circle-fill" animation="throb" font-scale="3"></b-icon>
		</div>
		<b-alert variant="danger" show v-else-if="!!error">{{error}}</b-alert>
		<b-form @submit.stop.prevent="save" id="channelEditForm" v-else>
			<b-avatar size="2em" :src="curChannel?.avatar_url||curChannel?.service_icon_url" badge-variant="" badge-offset="-0.4em">
				<template #badge><b-avatar v-if="curChannel?.avatar_url" :src="curChannel?.service_icon_url||null" size="1.3em" class="service_avatar" square :title="curChannel.service_name"></b-avatar></template>
			</b-avatar>
			{{curChannel.title}}
			<hr>
			<fieldset :disabled="saving > 0">
				<b-form-group label="Name" description="Enter the name or the URL of your channel" v-if="!readOnly">
					<VueTypeaheadBootstrap
						v-model="curChannel.title"
						:data="channelSuggestions"
						:serializer="item => `${item.title}`"
						@hit="setChannel($event)"
						placeholder="Search channels..."
						@input="channelSearch"
						disable-sort
						show-all-results
						show-on-focus
						autocomplete="off"
						required
					>
						<template slot="append">
							<b-input-group-text>
								<b-icon :icon="searching?'arrow-clockwise':'search'" :animation="searching?'spin':null"/>
							</b-input-group-text>
						</template>
						<template slot="suggestion" slot-scope="{ data }">
							<img
								v-if="data.thumbnail"
								class="rounded-circle mr-3 float-left"
								:src="data.thumbnail"
								style="width: 40px; height: 40px; float: left;"
							/>

							<div>{{data.name||data.token}}</div>
							<code v-if="data.name">{{data.token}}</code>
						</template>
					</VueTypeaheadBootstrap>
					<b-alert variant="danger" :show="!!searchError" class="mt-2 mb-0">{{searchError}}</b-alert>
				</b-form-group>
				<b-form-group label="Channel ID" v-if="!readOnly">
					<b-form-input v-model="curChannel.token" placeholder="Token" required readonly></b-form-input>
				</b-form-group>
				<b-form-group v-for="(field, i) in curChannel.protocolData?.channelSettingFields" :key="i" :label="field.type != 'bool' ? `${field.label||i}${field.required?' (required)':''}` : null">
					<b-form-input v-if="field.type == 'int' || field.type == 'float'" v-model.number="curChannel.data[i]" type="number"></b-form-input>
					<b-form-input v-else-if="field.type == 'color' || field.type == 'password' || field.type == 'email'" v-model="curChannel.data[i]" :type="field.type" :required="field.required"></b-form-input>
					<b-form-checkbox v-else-if="field.type == 'bool'" v-model="curChannel.data[i]" :value="true" :unchecked-value="false">{{field.label||i}}</b-form-checkbox>
					<b-form-checkbox-group v-else-if="field.type == 'checkList'" v-model="curChannel.data[i]" :options="field.options" stacked :state="curChannel.data[i]?.length > 0 ? null : false" invalid-feedback="Please select at least one option." :required="field.required && !curChannel.data[i]?.length"/>
					<b-form-input v-else v-model="curChannel.data[i]"></b-form-input>
				</b-form-group>
				<b-form-group label="Stream URL (RTMP)" v-if="user?.is_pro && !curChannel.protocolData?.noStreaming">
					<b-form-input v-model="curChannel.stream_url"></b-form-input>
				</b-form-group>
				<b-form-group label="Stream Key" v-if="user?.is_pro && !curChannel.protocolData?.noStreaming">
					<b-form-input type="password" v-model="curChannel.stream_key"></b-form-input>
				</b-form-group>
				<b-form-group label="Verify channel as yours" v-if="readOnly && !curChannel.verified">
					<b-input-group>
						<b-input ref="verificationCommand" v-model="curChannel.verificationCommand" @focus="$event.target.select()"></b-input>
						<b-input-group-append>
							<b-button v-if="curChannel.verificationCommand" variant="outline-secondary" title="Copy to clipboard" v-b-tooltip.hover @click="copyVerificationCommandToClipboard"><b-icon icon="clipboard"></b-icon></b-button>
							<b-button variant="primary" title="Generate" v-b-tooltip.hover @click="generateVerificationCommand">Generate</b-button>
						</b-input-group-append>
						<p class="text-muted">Without verification, you cannot use commands, timers and bridging in this channel. The bot will not send any messages to it.</p>
					</b-input-group>
					<b-form-text>{{curChannel.verificationCommandMessage}}</b-form-text>
				</b-form-group>
				<b-form-group label="Bridging" v-if="readOnly && curChannel.verified && user?.bridging_status == 'enabled'">
					<b-form-checkbox v-if="!curChannel.protocolData?.readOnly" v-model="curChannel.bridging_no_receive">Don't receive messages from other platforms</b-form-checkbox>
					<b-form-checkbox v-model="curChannel.bridging_no_send">Don't send messages to other platforms</b-form-checkbox>
				</b-form-group>
			</fieldset>
		</b-form>
		<template #modal-footer="{ ok, cancel }">
			<div class="w-100">
				<b-alert variant="danger" :show="!!savingError">{{savingError}}</b-alert>
				<div class="float-left">
					<a href="#" @click.prevent.stop="showDeleteModal()" class="text-danger"><b-icon icon="trash"></b-icon> Delete</a>
				</div>
				<div class="float-right">
					<b-spinner v-show="saving > 0" small label="Spinning" class="mr-3"></b-spinner>
					<b-button size="sm" class="mr-2" v-if="curChannel.auth" @click.prevent="openAuthPopup(curChannel.auth.url)" :href="curChannel.auth.url" target="_blank">Reauthenticate this channel <b-icon icon="arrow-up-right-square-fill"></b-icon></b-button>
					<b-button size="sm" variant="success" type="submit" form="channelEditForm" class="mr-2">
						Save
					</b-button>
					<b-button size="sm" @click="cancel()">
						Cancel
					</b-button>
				</div>
			</div>
		</template>
		<channel-delete-modal ref="channelDeleteModal" @deleted="$refs.modal.hide(); $emit('saved')"></channel-delete-modal>
	</b-modal>
</template>

<style>
	.vbt-autocomplete-list {
		z-index:  1055;
	}
</style>

<script>
import VueTypeaheadBootstrap from 'vue-typeahead-bootstrap';
import debounce from 'debounce';
import ChannelDeleteModal from './Delete';

export default {
	components: {
		VueTypeaheadBootstrap,
		ChannelDeleteModal,
	},
	data() { return {
		loading: 0,
		error: null,
		saving: 0,
		savingError: null,
		services: [],
		protocolData: null,
		readOnly: false,
		user: null,
		serviceId: null,
		serviceName: null,
		protocolName: null,
		channelToken: '',
		curChannel: {
			title: '',
			token: '',
			data: {},
		},
		searching: 0,
		channelSuggestions: [],
		searchError: null,
	}},
	methods: {
		channelSearch: debounce(function() {
			if(!this.serviceId) return;
			this.searching++;
			this.channelSuggestions = [];
			this.searchError = null;
			this.$api
				.get(`channels/${encodeURIComponent(this.serviceId)}/search`, { q: this.curChannel.title })
				.then(results => {
					this.channelSuggestions = results.map(r => ({ ...r, title: r.name||r.token }));
				}).catch(err => {
					console.error('Chanel search', err);
					this.channelSuggestions = [];
					this.searchError = err?.error||err;
				}).finally(() => {
					this.searching--;
				});
		}, 300),
		setChannel(item) {
			this.curChannel.token = item.token;
			this.curChannel.title = item.title;
			this.curChannel.data = item.data||{};
			this.curChannel.avatar_url = item.thumbnail;
		},
		async show(serviceId, channelToken, serviceName, protocolName) {
			try {
				this.loading++;
				this.error = null;
				this.saving = 0;
				this.savingError = null;
				this.channelSuggestions = [];
				this.serviceId = serviceId;
				this.serviceName = serviceName;
				this.protocolName = protocolName;
				this.channelToken = channelToken;
				this.curChannel = {
					title: '',
					token: '',
					data: {},
				};
				this.$refs.modal.show();
				this.user = await this.$api.get('userData');
				if(channelToken) {
					this.readOnly = true;
					const channel = await this.$api.get(`channel/${encodeURIComponent(serviceId)}/${encodeURIComponent(channelToken)}`);
					this.protocolName ||= channel.protocol_name;
					channel.data ||= {};
					this.curChannel = channel;
				} else {
					this.readOnly = false;
					const service = await this.$api.get(`service/${encodeURIComponent(serviceId)}`);
					this.curChannel.service_name = service.name;
					this.curChannel.service_id = serviceId;
					this.curChannel.protocolData = service.protocolData;
					this.curChannel.service_icon_url = service.icon_url;
				}
			} catch(e) {
				this.error = e;
			} finally {
				this.loading--;
			}
		},
		async save() {
			try {
				this.saving++;
				this.savingError = null;

				if(this.channelToken) {
					await this.$api.patch(`channel/${encodeURIComponent(this.serviceId)}/${encodeURIComponent(this.channelToken)}`, {
						data: this.curChannel.data,
						stream_url: this.curChannel.stream_url,
						stream_key: this.curChannel.stream_key,
					});
					this.$refs.modal.hide();
					this.$bvToast.toast('Successfully updated channel', {
						title: 'Success',
						variant: 'success',
						solid: true
					});
					this.$emit('saved');
				} else {
					await this.$api.post('channel', { data: {
						serviceId: this.serviceId,
						token: this.curChannel.token,
						title: this.curChannel.title,
						avatar_url: this.curChannel.avatar_url,
						data: this.curChannel.data,
						stream_url: this.curChannel.stream_url,
						stream_key: this.curChannel.stream_key,
					}});
					this.$refs.modal.hide();
					this.$bvToast.toast('Successfully added channel', {
						title: 'Success',
						variant: 'success',
						solid: true
					});
					this.$emit('saved');
				}
			} catch(e) {
				this.savingError = e;
			} finally {
				this.saving--;
			}
		},
		openAuthPopup(authUrl) {
			window.open(authUrl, 'Authenticate', 'scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=600,height=500');
		},
		generateVerificationCommand() {
			this.load(this.$api.get(`channel/${encodeURIComponent(this.curChannel.service_id)}/${encodeURIComponent(this.curChannel.token)}/verificationCode`)).then(result => {
				this.curChannel.verificationCommand = `!verify ${result.code}`;
				this.curChannel.verificationCommandMessage = `Post this command, as the channel owner, in your chat. Expires in ${result.expiryMinutes} minutes.`;
			});
		},
		copyVerificationCommandToClipboard() {
			this.$refs.verificationCommand.focus();
			document.execCommand('copy');
		},
		showDeleteModal() {
			this.$refs.channelDeleteModal.show(this.curChannel.service_id, this.curChannel.token, this.curChannel.service_name, this.curChannel.protocol_name);
		},
	}
};
</script>
